// The deployment dashboard: one row per site the daily run works, in the planned order.
// Mirrors lambda_handlers/api/stateful_agents/portfolio/{dashboard,dashboard_move,project_attributes}.py.
import { useMutation, useQuery, useQueryClient } from "react-query";

import type { NextMove } from "./useProjectAgentStatus";
import { useAgentsHttp } from "../useAgentsHttp";
import type { NextStepOwner, OnTrack } from "./useAgentPortfolio";
import { QK } from "../queryKeys";

// A tracked project attribute (project_attributes.PROJECT_ATTRIBUTES).
export interface AttributeDef {
  key: string;
  label: string;
  // What it is, in a sentence.
  describe: string;
  kind: "number" | "date" | "stage" | "person";
  unit: string;
  choices: string[];
  choice_labels: Record<string, string>;
  // A number is stored in `unit` and shown as value * display_scale in display_unit.
  display_scale: number;
  display_unit: string;
  decimals: number;
  // Shown against the project's target size: below it reads as a shortfall.
  compare_to_target: boolean;
  // Applies only to some projects (a battery).
  optional: boolean;
  // Non-empty: an option may carry no figure (a null value), shown as this.
  no_figure: string;
}

// A person attribute's value (project_attributes.PERSON_FIELDS); org is always set.
export interface PersonValue {
  name: string;
  org: string;
  email: string;
}

// null: an option with no figure, its label saying why (AttributeDef.no_figure).
export type OptionValue = number | string | PersonValue | null;

// One view of an attribute: a single option once settled, several while a choice is open.
export interface AttributeValue {
  entry_id: string;
  project_id: string;
  attribute: string;
  // ``limited_by``: the specialists whose limits an achievable option applies.
  options: { value: OptionValue; label: string; limited_by?: string[] }[];
  reason: string;
  set_by_agent: string;
  set_by_run_id: string | null;
  set_at: string;
  // Where a value no agent worked out came from (set_by_agent "migration"), shown in
  // place of the agent and run; null for an agent's view.
  origin: string | null;
  // What the options' limits account for ("grid, structural", "no limits found"); ""
  // where none were recorded (project_attributes.accounts_for).
  accounts_for: string;
}

// A committed size as the linked platform site gives it (project_attributes.PitchedFigure).
export interface PitchedFigure {
  value: number;
  site_id: string;
}

// Where a DC/AC ratio came from (achievable_kwp.Basis): the max-out design's kWp over the
// original grid application's PV kWac, a design's on record, the PPA's, or the platform's.
export type RatioBasis = "max_out" | "committed" | "proposed";

// How an achievable kWp figure was inferred from its route's grid kWac
// (achievable_kwp.infer_kwp_options): the kWac, the DC/AC ratio and where the ratio came
// from, with the two figures it was taken from where it is not a design's own.
export interface InferredKwp {
  kw_ac: number;
  ratio: number;
  basis: RatioBasis;
  dc_kwp: number | null;
  ac_kw: number | null;
}

// One achievable kWp option as the backend reads it: the recorded option, its figure
// inferred where the recorded one has none.
export interface KwpOption {
  value: number | null;
  label: string;
  limited_by?: string[];
  inferred: InferredKwp | null;
}

export type RagVerdict = "green" | "amber" | "red";

// The four tests the platform computes (project_rag.rag_tests).
export interface RagTests {
  verdict: RagVerdict;
  tests: Record<string, { verdict: RagVerdict; detail: string }>;
  /** The earlier four tests, beside the verdict while the two are compared. */
  legacy?: {
    verdict: RagVerdict;
    tests: Record<string, { verdict: RagVerdict; detail: string }>;
  };
}

// The decision card and draft email an item points at; absent on a summary recorded
// before items carried links.
export interface ItemLinks {
  decision_id?: string;
  email_id?: string;
}

// A decision card or draft email a summary item links to, possibly on another project.
export interface LinkedItem {
  kind: "decision" | "email";
  project_id: string | null;
  project_name: string;
  run_id: string;
  label: string;
  // open waits on a person; closed (a decision cleared), sent, or dropped (an email
  // withdrawn or retired) no longer do.
  state: "open" | "closed" | "sent" | "dropped";
  // A decision: the specialist whose Next actions shows it, and the action holding it.
  agent: string;
  action_id: string;
}

// The Manager's summary by reference (project_summary.validate_reference_summary): the
// text, owner, status and age of each item come from the record in ``row.records``.
export interface ReferenceSummary {
  format: 2;
  rag: { verdict: RagVerdict; reason: string; override_reason: string };
  blockers: string[];
  next_steps: string[];
  /** Every open action, first to last across the team, each with why it ranks there.
   * Absent on a summary written before the Manager ranked every action. */
  ranking?: { action_id: string; why: string }[];
}

export const byReference = (
  summary: ProjectSummary | ReferenceSummary,
): summary is ReferenceSummary => (summary as ReferenceSummary).format === 2;

/** An action or date a summary references, as the dashboard shows it. */
export interface RecordedAction {
  action_id: string;
  agent: string;
  type: "contact" | "decision" | "task";
  /** Who outside InRange a contact action asks. */
  external: string;
  title: string;
  /** The dashboard line: what the row shows, a sentence or two that stands alone. */
  summary: string;
  category: string;
  category_name: string;
  owner: string;
  /** The date its Next actions row shows: when the reply was due for an email awaiting
   * one, else its own due date. */
  due: string;
  status: string;
  late: boolean;
  blocking_since: string;
  /** Why the Manager ranks it where it does, when it is one of the summary's picks. */
  pick_reason?: string;
  waits_for: string;
  /** Who a waiting item is on, when its wait names them. */
  waits_on?: { on: "us" | "them"; name: string; org: string } | null;
}

export interface RecordedDate {
  date_id: string;
  agent: string;
  what: string;
  date: string;
  hardness: string;
}

// A summary from before summaries referenced records (project_summary.validate_summary).
export interface ProjectSummary {
  rag: { verdict: RagVerdict; reason: string; override_reason: string };
  blockers: ({
    id: string;
    text: string;
    owner: string;
    waiting_on: string;
    clears_when: string;
    since: string;
  } & ItemLinks)[];
  // `by` is a date the record gives; `target` our own where it gives none (absent on
  // summaries written before it existed).
  next_actions: ({
    text: string;
    owner: string;
    by: string;
    target?: string;
  } & ItemLinks)[];
  next_gate: {
    what: string;
    date: string;
    criteria_met: boolean | null;
  } | null;
}

/** A count of a project's actions held by one agent. */
export interface AgentCount {
  agent: string;
  label: string;
  count: number;
}

// A specialist's status row as its card on the Status page leads with it: the verdict,
// the stage and who owes the next move (dashboard_rows._status_chips).
export interface SpecialistChips {
  lifecycle: string;
  next_step_owner: NextStepOwner;
  /** Who owes the next move, from the agent's blocking action. Absent on a row
   * snapshot taken before rows carried it. */
  next_move?: NextMove;
  on_track: OnTrack;
}

export interface DashboardRow {
  project_id: string;
  name: string;
  // The BMA organisations, each with its role ("Tenant", "Landlord", "Owner-occupier").
  parties: { name: string; role: string }[];
  target_kwp: number | null;
  attributes: Record<string, AttributeValue>;
  // By committed attribute key: the platform's figure, "Proposed" where nothing is
  // committed, and behind "different from proposed" where a committed figure disagrees.
  pitched: Record<string, PitchedFigure>;
  /** Achievable kWp's options with any grid-only route's figure inferred; null where
   * none is recorded and none can be inferred. Absent on a row kept from before the
   * backend inferred it: read it with achievableKwpOptions. */
  achievable_kwp?: KwpOption[] | null;
  bma_site_id: string | null;
  // The linked site's name on admin.inrange.io; `name` prefers the operator's friendly name.
  bma_site_name: string | null;
  // The day that site was last saved on admin.inrange.io; null until a refresh records it.
  bma_site_updated_on: string | null;
  /** That site's operationalStatus as BMA stores it ("offerMade"); null until a refresh
   * records it. Absent on a row snapshot taken before rows carried it. */
  bma_status?: string | null;
  /** Each specialist's status row as its card on the Status page leads with it, in team
   * order: those with a dashboard column always (null before a row), the rest where
   * they work the project. Absent on a row snapshot kept before rows carried it, which
   * has the chips under each agent's key instead: read them with rowSpecialists. */
  specialists?: Record<string, SpecialistChips | null>;
  epc_party: string | null;
  summary: {
    summary: ProjectSummary | ReferenceSummary;
    set_at: string;
    set_by_run_id: string | null;
  } | null;
  rag: RagTests;
  // Every decision card and draft email the summary's items link to, by id.
  links: Record<string, LinkedItem>;
  /** Every open action on the project that its pages colour red. Absent on a row kept
   * from before it was counted. */
  overdue?: number;
  /** Those actions counted by the agent whose list holds them, in team order, agents
   * with none left out. Absent on a row kept from before it was counted. */
  overdue_by_agent?: AgentCount[];
  /** Every open action on the project that its pages colour yellow (due soon), in total
   * and by agent as above. Absent on a row kept from before it was counted. */
  upcoming?: number;
  upcoming_by_agent?: AgentCount[];
  /** The records a by-reference summary names, and every open blocking action. Absent
   * on a row snapshot taken before rows carried them. */
  records?: {
    actions: Record<string, RecordedAction>;
    dates: Record<string, RecordedDate>;
    blocking: RecordedAction[];
  };
  waiting: { decisions: number; emails: number };
  latest_run_id: string | null;
  applying_review: ApplyingReview | null;
  /** Review comments waiting on the operator's answer, and the conversations to give it
   * in, oldest first. */
  review_waiting: { count: number; conversation_ids: string[] } | null;
  /** Agent questions waiting on the operator across the site's specialist tabs, each
   * once. Absent on a row kept from before it was counted. */
  feedback_open?: number;
}

/** A project's review comments in flight, and the conversation that holds them. */
export interface ApplyingReview {
  conversation_id: string | null;
  comment_ids: string[];
  phases: Record<string, ReviewPhase>;
  /** The specialists whose lists the comments are about; absent on a row saved before it
   * was sent, and empty for comments on the project as a whole. */
  agents?: string[];
  /** A comment names no specialist: any of the project's lists may move. */
  whole_project?: boolean;
}

/** Queued until its conversation is free; answering while the agent applies it. */
export type ReviewPhase = "queued" | "answering";

// The specialists a snapshot kept before rows carried `specialists` could hold, each
// under its own key on the row.
const SNAPSHOT_SPECIALIST_KEYS = ["legal", "grid", "building_readiness", "design_readiness"];

/** The row's specialist chips by agent, from a row of today or a snapshot kept before
 * rows carried `specialists`. */
export const rowSpecialists = (
  row: DashboardRow,
): Record<string, SpecialistChips | null> => {
  if (row.specialists) return row.specialists;
  const kept = row as unknown as Record<string, SpecialistChips | null | undefined>;
  return Object.fromEntries(
    SNAPSHOT_SPECIALIST_KEYS.filter((agent) => kept[agent] !== undefined).map(
      (agent) => [agent, kept[agent] ?? null],
    ),
  );
};

// While review comments are in flight, poll so the page sees each one land.
export const APPLYING_POLL_MS = 5_000;

export interface Dashboard {
  attributes: AttributeDef[];
  projects: DashboardRow[];
}


// Every project the daily run works, or one project's own row.
export const useDashboard = (projectId?: string) => {
  const http = useAgentsHttp();
  return useQuery<Dashboard>(
    [QK.dashboard, projectId ?? null],
    () =>
      http<Dashboard>(
        "/dashboard/projects",
        projectId ? { project_ids: [projectId] } : {},
      ),
    {
      refetchInterval: (data) =>
        data?.projects.some((p) => p.applying_review)
          ? APPLYING_POLL_MS
          : false,
    },
  );
};

export const useMoveDashboardRow = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    { order: string[] },
    Error,
    // `to` is the index the row lands on once taken out of the order.
    { project_id: string; to: number; actor_email: string }
  >((body) => http<{ order: string[] }>("/dashboard/move", body), {
    // Reorder in place at once; the refetch confirms it.
    onMutate: async ({ project_id, to }) => {
      // An in-flight refetch landing after this would put the row back.
      await qc.cancelQueries([QK.dashboard]);
      qc.setQueryData<Dashboard | undefined>(
        [QK.dashboard, null],
        (current) => {
          if (!current) return current;
          const rows = [...current.projects];
          const at = rows.findIndex((r) => r.project_id === project_id);
          if (at < 0) return current;
          const [row] = rows.splice(at, 1);
          rows.splice(Math.max(0, Math.min(to, rows.length)), 0, row);
          return { ...current, projects: rows };
        },
      );
    },
    onSettled: () => void qc.invalidateQueries([QK.dashboard]),
  });
};

export const useProjectAttributeHistory = (projectId: string | null) => {
  const http = useAgentsHttp();
  return useQuery<{
    project_id: string;
    attributes: AttributeDef[];
    history: AttributeValue[];
  }>(
    [QK.projectAttributes, projectId],
    () => http("/projects/attributes", { project_id: projectId }),
    { enabled: !!projectId },
  );
};
